import styled from "@emotion/styled";
import type { FC, ReactNode } from "react";

import { MDSButton, MDSButtonVariant, MDSCard, MDSCardSlotName, MDSLoadingSpinner } from "@/mds";
import { FeatureEnrollmentPageViewKind } from "@/pages/feature-enrollment/feature-enrollment.types";
import type { FeatureEnrollmentLinkSummary } from "@/pages/feature-enrollment/feature-enrollment.utils";

interface FeatureEnrollmentContentProps {
  viewKind: FeatureEnrollmentPageViewKind;
  link: FeatureEnrollmentLinkSummary | null;
  isAccepting: boolean;
  acceptError: string | null;
  statusTitle: string | null;
  statusBody: string | null;
  viewerName: string | null;
  onAccept: () => void;
  onContinue: () => void;
  onDismiss: () => void;
}

export const FeatureEnrollmentContent: FC<FeatureEnrollmentContentProps> = ({
  viewKind,
  link,
  isAccepting,
  acceptError,
  statusTitle,
  statusBody,
  viewerName,
  onAccept,
  onContinue,
  onDismiss,
}) => {
  if (viewKind === FeatureEnrollmentPageViewKind.Loading) {
    return (
      <CardContainer>
        <LoadingCard>
          <MDSLoadingSpinner size={24} />
        </LoadingCard>
      </CardContainer>
    );
  }

  if (viewKind === FeatureEnrollmentPageViewKind.Ready && link) {
    return (
      <FeatureEnrollmentCard
        title={`Enable ${link.title}`}
        description={getReadyDescription({ link, viewerName })}
      >
        <ActionsWrapper>
          <ActionsRow>
            <MDSButton
              variant={MDSButtonVariant.Contrast}
              onPress={onAccept}
              isLoading={isAccepting}
            >
              Enable feature
            </MDSButton>
          </ActionsRow>
          {acceptError ? <ErrorText>{acceptError}</ErrorText> : null}
        </ActionsWrapper>
      </FeatureEnrollmentCard>
    );
  }

  if (viewKind === FeatureEnrollmentPageViewKind.AlreadyEnrolled && link) {
    return (
      <FeatureEnrollmentCard
        title="You're already enrolled"
        description={`${link.title} is already enabled for your Mem account.`}
      >
        <ActionsRow>
          <MDSButton variant={MDSButtonVariant.Contrast} onPress={onContinue}>
            Continue
          </MDSButton>
        </ActionsRow>
      </FeatureEnrollmentCard>
    );
  }

  return (
    <FeatureEnrollmentCard
      title={statusTitle ?? "We couldn't open this feature enrollment link."}
      description={statusBody}
    >
      <ActionsRow>
        <MDSButton variant={MDSButtonVariant.Contrast} onPress={onDismiss}>
          Go to Mem
        </MDSButton>
      </ActionsRow>
    </FeatureEnrollmentCard>
  );
};

const getReadyDescription = ({
  link,
  viewerName,
}: {
  link: FeatureEnrollmentLinkSummary;
  viewerName: string | null;
}): string => {
  const description = link.description?.trim();

  if (description) {
    return description;
  }

  if (viewerName) {
    return `Turn this on for ${viewerName}.`;
  }

  return "Turn this on for your Mem account.";
};

const CardContainer = styled.div`
  width: min(640px, 100%);
  min-width: 320px;
  min-height: 208px;
`;

const ModalCard = styled(MDSCard)`
  padding: ${({ theme }) => theme.spacing[7]};
  background: ${({ theme }) => theme.colors.surface.modal};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
`;

const FeatureEnrollmentCard = ({
  title,
  description,
  children,
}: {
  title: string;
  description: string | null;
  children: ReactNode;
}) => (
  <CardContainer>
    <ModalCard title={title} description={description ?? undefined}>
      <div slot={MDSCardSlotName.Content}>{children}</div>
    </ModalCard>
  </CardContainer>
);

const LoadingCard = styled.div`
  min-height: 208px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
`;

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[6]};
  width: 100%;
`;

const ErrorText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.danger};
  font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
  line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
`;
